import { useCallback, useEffect, useState } from "react";

import i18n from "./index";
import { useLanguagePersistence } from "./language-persistence";
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, normalizeLanguage } from "./languages";
import type { SupportedLanguage } from "./languages";
import { clearStoredLanguage, readStoredLanguageSource, writeStoredLanguage } from "./storage";
import type { StoredLanguageSource } from "./storage";

export interface UseLanguage {
  /** The active language code — always one we ship. */
  language: string;
  /** Change the language everywhere: i18next, localStorage, the backend. */
  setLanguage: (code: string) => Promise<void>;
  /** Everything the picker should offer. */
  languages: readonly SupportedLanguage[];
}

/**
 * The one way to read or change the app's language.
 *
 * Precedence, and why — the same order the backend applies to its own content
 * (user preference > workspace default > Accept-Language > English):
 *   - **The user's own language** wins. It is the account-level truth and
 *     follows them between browsers.
 *   - **The workspace default** applies to anyone who has never picked one.
 *     Settings → Workspace promises exactly that: "the language members see
 *     until they pick their own".
 *   - **The browser's languages**, then English, are the last resorts, handled
 *     by the i18next detector at init.
 *
 * localStorage is not a layer in that order — it is a *cache of the answer*,
 * applied synchronously at i18next init (see `./index.ts`) so a returning user
 * is already looking at the right language before React mounts, and overwritten
 * by the account reads a beat later. It deliberately has no veto: the picker it
 * inherited the key from was a `useStickyState` that wrote its `"en"` default on
 * mount, so a stored `"en"` is as likely to be an artifact of visiting Settings
 * once as it is a decision, and letting it outrank the workspace default would
 * have silently excluded most existing users from the feature.
 *
 * Reads `i18n.language` rather than holding its own copy of the value, and
 * subscribes to `languageChanged`, so every consumer stays in step no matter
 * which one made the change.
 */
/**
 * The workspace whose account language has already been applied, or
 * deliberately overridden by the user, during this page session. Null while no
 * workspace has been settled yet.
 *
 * Module-level on purpose: see the effect in `useLanguage`. Nothing but a page
 * load or a workspace switch should clear it, and a page load reloads this
 * module.
 *
 * Keyed by workspace rather than a bare boolean because language preferences
 * are per-workspace on both sides — `user_preferences` is keyed by
 * (workspace, user), and the default belongs to one workspace. Switching
 * workspaces does not reload the page, so a boolean left the second workspace
 * showing the first one's language.
 */
let appliedForWorkspace: string | null = null;

/** Test seam: reset the once-per-session guard between cases. */
export function __resetRemoteLanguageGuardForTests(): void {
  appliedForWorkspace = null;
}

/**
 * Apply an account-level language — or the absence of one — to i18next and the
 * boot cache.
 *
 * Shared with `WorkspaceLanguageSelect`, which needs exactly this the moment an
 * admin changes the workspace default: for an admin who has no language of
 * their own, the default they just set is now theirs, and having to reload to
 * see it is the same thing that made the setting look broken in the first place.
 */
export function applyAccountLanguage(
  code: string | null,
  source: StoredLanguageSource,
): void {
  const next = normalizeLanguage(code);
  if (!next) {
    // No language at either layer. If what is cached came from a workspace
    // default that has since been cleared, retire it and let the detector fall
    // back to the browser — otherwise a cleared default would go on applying
    // forever with nothing left to correct it. A personal choice is never
    // touched here.
    if (readStoredLanguageSource() === "workspace") {
      clearStoredLanguage();
      void i18n.changeLanguage();
    }
    return;
  }

  // Cache the answer so the next boot applies it without a round-trip, and
  // remember which layer it came from (see `./storage.ts`).
  writeStoredLanguage(next, source);
  if (next === normalizeLanguage(i18n.language)) return;
  void i18n.changeLanguage(next);
}

export function useLanguage(): UseLanguage {
  const { remoteLanguage, remoteSource, remoteSettled, workspaceId, persistLanguage } =
    useLanguagePersistence();
  const [language, setLanguageState] = useState<string>(
    () => normalizeLanguage(i18n.language) ?? DEFAULT_LANGUAGE,
  );

  useEffect(() => {
    const handleChange = (next: string) => {
      setLanguageState(normalizeLanguage(next) ?? DEFAULT_LANGUAGE);
    };
    i18n.on("languageChanged", handleChange);
    // i18next may have resolved a language between render and subscribe.
    handleChange(i18n.language);
    return () => {
      i18n.off("languageChanged", handleChange);
    };
  }, []);

  // Apply the account's language — the user's own, else the workspace default —
  // once per workspace.
  //
  // The guard is module-level, NOT a ref, and that is the whole point.
  // `AppLayout` renders the routed content as `<main key={i18n.language}>`, so
  // changing language remounts every consumer of this hook — which reset a ref
  // to `false` and let this effect run again. It then re-applied the cached
  // account value on top of the choice the user had just made, so switching
  // away from a saved language snapped straight back to it and the UI looked
  // stuck. "Has the account language been applied / overridden?" is a fact
  // about this page session, not about a component instance, so it has to live
  // where a remount cannot clear it.
  useEffect(() => {
    const scope = workspaceId ?? "";
    if (appliedForWorkspace === scope) return;
    // Nothing is decided until both preference reads are in; acting early would
    // burn the guard on a half-loaded picture.
    if (!remoteSettled) return;
    appliedForWorkspace = scope;

    applyAccountLanguage(remoteLanguage, remoteSource ?? "user");
  }, [remoteLanguage, remoteSource, remoteSettled, workspaceId]);

  const setLanguage = useCallback(
    async (code: string) => {
      const next = normalizeLanguage(code);
      // A language we don't ship is ignored rather than fallen back to English:
      // silently switching someone off their current language would be worse
      // than doing nothing.
      if (!next) return;
      // A deliberate choice ends the "hydrate from the backend" window for this
      // workspace, even if the preferences queries are still in flight.
      appliedForWorkspace = workspaceId ?? "";
      writeStoredLanguage(next, "user");
      await i18n.changeLanguage(next);
      await persistLanguage(next);
    },
    [persistLanguage, workspaceId],
  );

  return { language, setLanguage, languages: SUPPORTED_LANGUAGES };
}
